import React, { Component } from "react";
import { IUserDetails } from "../Interface";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment
} from "@mui/material";
import UserDetailsService from "../../../Services/UserDetailsService";
import { ShowAlert } from "../../../Component/ShowAlert";
import AlertDialogPopop from "../../../Component/AlertDialogPopop/AlertDialogPopop";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import ValidationCheck from "../../../Common/Validation/ValidationChecks";
import WarningIcon from '@mui/icons-material/Warning';
import TextMaskCustom from "../../../Component/TextMaskCustom/TextMaskCustom";

export enum ActionButtons {
    Save = 'save',
    Cancel = 'cancel'
}

// Props interface for EditUserDetails component
interface EditUserDetailsProps {
    open: boolean;
    onClose?: (state: boolean, action: string) => void;
    selectedUserData?: IUserDetails;
    accessToken: string;
    refreshData?: () => void;
}

interface UserIdentityDoc {
    filename: string;
    userIdentity: string;
    byteSize: number;
}

// State interface for EditUserDetails component
interface EditUserDetailsState {
    firstName: string;
    lastName: string;
    dateOfBirth: Dayjs | null;
    email: string;
    mobileNumber: any;
    originalEmail: string;
    originalMobileNumber: string;
    isEmailFocused: boolean;
    isMobileNumberError: boolean;
    isModified: boolean;
    userId: string;
    isSaveUserDetails: boolean;
    isFirstNameError: boolean;
    isLastNameError: boolean;
    isDateOfBirthError: boolean;
    personaId: string;
    userIdentityDoc: UserIdentityDoc;
}

class EditUserDetails extends Component<EditUserDetailsProps, EditUserDetailsState> {
    constructor(props: EditUserDetailsProps) {
        super(props);
        this.state = {
            firstName: props.selectedUserData?.firstName || "",
            lastName: props.selectedUserData?.lastName || "",
            dateOfBirth: props.selectedUserData?.dob && this.isValidDateFormat(props.selectedUserData.dob)
                ? dayjs(props.selectedUserData.dob, 'MM/DD/YYYY')
                : null,
            email: props.selectedUserData?.email || "",
            mobileNumber: props.selectedUserData?.mobile_number || "",
            originalEmail: props.selectedUserData?.email || "",
            originalMobileNumber: props.selectedUserData?.mobile_number || "",
            userId: props.selectedUserData?.user_id || "",
            personaId: props.selectedUserData?.personaId || "",
            userIdentityDoc: {} as UserIdentityDoc,
            isSaveUserDetails: false,
            isFirstNameError: false,
            isLastNameError: false,
            isDateOfBirthError: false,
            isEmailFocused: false,
            isMobileNumberError: false,
            isModified: false
        };
    }

    isValidDateFormat = (dateStr: string) => {
        return dayjs(dateStr, 'MM/DD/YYYY', true).isValid();
    };

    componentDidMount() {
        this.getUserPersonaIdentityDocument();
    }

    // getUserPersonaIdentityDocument
    private async getUserPersonaIdentityDocument() {
        try {
            if (!this.state.personaId) return;
            // API call to get user persona identity document
            const response = await UserDetailsService.getUserPersonaIdDoc(this.props.accessToken, this.state.personaId);

            if (!response.ok) {
                throw new Error("Failed to fetch persona identity document");
            }

            const data = await response.json();

            if (data?.persona_government_id) {
                this.setState(
                    {
                        userIdentityDoc: {
                            filename: data.persona_government_id.filename || "",
                            userIdentity: data.persona_government_id.url || "",
                            byteSize: data.persona_government_id["byte-size"] || 0,
                        },
                    },
                    () => console.log("User Identity Document:", this.state.userIdentityDoc)
                );
            }
        } catch (error) {
            ShowAlert("Error", "Failed to fetch persona identity document. Please try again later.", "error");
            console.error("Error fetching identity document:", error);
        }
    }


    // Convert string to Title Case
    toTitleCase = (str: string) => {
        return str
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    // Handle input field changes
    handleChange = (field: keyof EditUserDetailsState, value: string | Dayjs | null) => {
        if (field === "dateOfBirth") {
            this.setState({
                dateOfBirth: value as Dayjs | null,
                isDateOfBirthError: !value,
                isModified: true
            });
        } else {
            const formattedValue = this.toTitleCase(value as string);
            const errorField = `is${field.charAt(0).toUpperCase() + field.slice(1)}Error` as keyof EditUserDetailsState;

            this.setState(prevState => ({
                ...prevState,
                [field]: formattedValue,
                [errorField]: !(value as string).trim(),
                isModified: true
            }));
        }
    };

    //handle form submission on save
    private async handleSave(action: string) {
        if (action === ActionButtons.Save) {
            const { firstName, lastName, dateOfBirth, email, mobileNumber, originalEmail, originalMobileNumber, userId } = this.state;

            const isFirstNameError = !firstName.trim();
            const isLastNameError = !lastName.trim();
            const isDateOfBirthError = !dateOfBirth;
            const formattedMobile = mobileNumber ? mobileNumber.replace(/^(\+1[-\s]?)/, '') : mobileNumber;

            if (isFirstNameError || isLastNameError || isDateOfBirthError) {
                this.setState({ isFirstNameError, isLastNameError, isDateOfBirthError });
                return;
            }
            try {
                const formattedDate = dateOfBirth?.format('MM/DD/YYYY') || "";
                const emailChanged = email !== originalEmail;
                const mobileChanged = mobileNumber !== originalMobileNumber;

                // Validate email and phone if either changed
                if (emailChanged || mobileChanged) {
                    try {

                        const validatePayload: {
                            userId: string;
                            email?: string | null;
                            mobileNumber?: string | null;
                        } = {
                            userId,
                            email: emailChanged ? email : null,
                            mobileNumber: mobileChanged ? formattedMobile : null,
                        };

                        const response = await UserDetailsService.validateEmailAndMobile(this.props.accessToken, validatePayload);
                        const res = await response.json();
                        switch (res.result) {
                            case 'email_exists':
                                ShowAlert('Warning', 'Email already exists for another user.', 'warning');
                                return;
                            case 'mobile_exists':
                                ShowAlert('Warning', 'Mobile number already exists for another user.', 'warning');
                                return;
                            case 'both_exist':
                                ShowAlert('Warning', 'Both email and mobile number already exist for other users.', 'warning');
                                return;
                        }
                    } catch (err) {
                        console.error("Validation error:", err);
                        ShowAlert("Error", "Failed to validate email and mobile number.", "error");
                        return;
                    }
                }

                const postData = {
                    userId,
                    firstName,
                    lastName,
                    dob: formattedDate,
                    email: emailChanged ? email : null,
                    mobileNumber: formattedMobile,
                };
                // Call API to update user details
                const response = await UserDetailsService.updateUserDetails(this.props.accessToken, postData);
                if (response.status === 200) {
                    this.props.onClose && this.props.onClose(false, action);
                    this.props.refreshData?.();
                    ShowAlert("Success", "User details updated successfully!", "success");
                } else {
                    throw new Error();
                }
            } catch (error) {
                console.error("Error updating user details:", error);
                ShowAlert("Error", "Failed to update user details. Please try again.", "error");
            }
        } else if (this.props.onClose) {
            this.props.onClose(false, action);
        }
    }

    render() {
        const { open, onClose } = this.props;
        let { firstName, lastName, dateOfBirth, isSaveUserDetails, isFirstNameError, isLastNameError, isDateOfBirthError, userIdentityDoc,
            email, mobileNumber, isEmailFocused, isMobileNumberError } = this.state;

        // validate Email    
        const isEmailEmpty = isEmailFocused && email === "";
        const isEmailInvalid = isEmailFocused && email !== "" && !ValidationCheck.validateEmail(email);
        const isEmailError = isEmailEmpty || isEmailInvalid;

        mobileNumber = mobileNumber ? mobileNumber.replace('+1', ''): mobileNumber;
        const isButtonDisabled = isFirstNameError || isLastNameError || isDateOfBirthError || isEmailError || isMobileNumberError;
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
                    <DialogTitle>Verify User Identity</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={8}>
                                <Box className="custom-container-img">
                                    {userIdentityDoc.userIdentity ? (
                                        <img
                                            src={userIdentityDoc.userIdentity}
                                            alt="User Identity"
                                            className="customImage-size"
                                        />
                                    ) : (
                                        <p>{this.state.personaId ? "Loading document..." : "No Image Available"}</p>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    value={firstName}
                                    onChange={(e) => this.handleChange("firstName", e.target.value)}
                                    margin="dense"
                                    error={isFirstNameError}
                                    helperText={isFirstNameError ? "First name is required" : ""}
                                />
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    value={lastName}
                                    onChange={(e) => this.handleChange("lastName", e.target.value)}
                                    margin="dense"
                                    error={isLastNameError}
                                    helperText={isLastNameError ? "Last name is required" : ""}
                                />
                                <DesktopDatePicker
                                    label="Date of Birth (MM/DD/YYYY)"
                                    value={dateOfBirth}
                                    onChange={(newValue) => this.handleChange("dateOfBirth", newValue)}
                                    maxDate={dayjs()}
                                    slotProps={{
                                        textField: {
                                            margin: 'dense',
                                            fullWidth: true,
                                            error: isDateOfBirthError,
                                            helperText: isDateOfBirthError ? "Date of birth is required" : "",
                                        },
                                        desktopPaper: {
                                            sx: {
                                                '& .MuiPickersDay-root.Mui-selected': {
                                                    backgroundColor: (theme) => `${theme.palette.secondary.main} !important`,
                                                    color: (theme) => theme.palette.secondary.contrastText,
                                                },
                                                '& .MuiPickersDay-root.Mui-selected:hover': {
                                                    backgroundColor: (theme) => `${theme.palette.secondary.dark} !important`,
                                                },
                                            },
                                        },
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Email"
                                    value={email}
                                    onChange={(text) => {
                                        this.setState({
                                            isModified: true,
                                            isEmailFocused: true,
                                            email: text.target.value
                                        });
                                    }}
                                    margin="dense"
                                />
                                {
                                    (this.state.isEmailFocused && this.state.email !== "" && !ValidationCheck.validateEmail(this.state.email)) &&
                                    <div className="error-msg"><WarningIcon /> Please enter a valid email address</div>
                                }
                                {
                                    (this.state.isEmailFocused && this.state.email === "") &&
                                    <div className='error-msg'><WarningIcon /> Please enter the email</div>
                                }
                                <FormControl
                                    variant="outlined"
                                    fullWidth
                                    margin="dense"
                                >
                                    <InputLabel htmlFor="mobile-number-input">Mobile Number</InputLabel>
                                    <OutlinedInput
                                        id="mobile-number-input"
                                        name="mobileNumber"
                                        value={mobileNumber}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            this.setState({
                                                isModified: true,
                                                mobileNumber: value,
                                                isMobileNumberError: !ValidationCheck.validateMobileNumber(value),
                                            });
                                        }}
                                        label="Mobile Number"
                                        startAdornment={<InputAdornment position="start">+1</InputAdornment>}
                                        inputComponent={TextMaskCustom as any}
                                        inputProps={{
                                            'aria-label': 'mobile number',
                                        }}
                                    />
                                    {
                                        (this.state.isMobileNumberError && this.state.mobileNumber !== "" && !ValidationCheck.validateMobileNumber(this.state.email)) &&
                                        <div className="error-msg"><WarningIcon /> Please enter a valid mobile number</div>
                                    }
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.handleSave(ActionButtons.Cancel)}>Cancel</Button>
                        <Button
                            color="secondary"
                            variant="contained"
                            disabled={isButtonDisabled}
                            onClick={() => {
                                if (!this.state.isModified) {
                                    ShowAlert("Info", "No changes to save.", "info");
                                } else {
                                    this.setState({ isSaveUserDetails: true });
                                }
                            }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                    <AlertDialogPopop
                        open={isSaveUserDetails}
                        alertTitle="Confirmation"
                        alertBody="Are you sure you want to save the details?"
                        onClose={(_, action_) => {
                            this.setState({ isSaveUserDetails: false }, () => {
                                if (action_ === "ok") {
                                    this.handleSave(ActionButtons.Save);
                                }
                            });
                        }}
                    />
                </Dialog>
            </LocalizationProvider >
        );
    }
}

export default EditUserDetails;